// src/pages/ManageLeague.js
import React, { useEffect, useState } from "react";
import { getAuth } from "firebase/auth";
import { useParams, useNavigate } from "react-router-dom";
import Button from "../central/Button";
import { getDoc, getDocs, query, where, updateDoc, doc, setDoc, deleteField, collection } from "firebase/firestore";
import { db } from "../lib/firebase";
import { paths } from "../lib/paths";
import { col, ref } from "../lib/fw";
import { computeMatchResult, RESULT_COLORS } from "../components/matchInput/MatchScoreStep";

import { calculatePointsFromMatches } from "../utils/calculatePointsFromMatches";
import { calculateTeamPoints } from "../utils/calculateTeamPoints";
import { advanceGameweek } from "../utils/advanceGameweek";

import Dropdown from "../central/Dropdown";

import Panel from "../central/Panel";

import { logError } from "../utils/logError";

import { useToast } from "../context/ToastContext";

import { setActiveSeasonID } from "../lib/seasonPaths";
import PageLoader from "../components/PageLoader";
import LoadingOverlay from "../central/LoadingOverlay";

import { ChevronDown, ChevronUp, Info, AlertCircle } from "lucide-react";


// Defined outside ManageLeague so it has a stable identity across renders.
// If defined inside, React treats it as a new component type on every render,
// unmounting and remounting children (including inputs) on every state change.
const CollapsibleSection = ({ title, isOpen, onToggle, children }) => (
  <Panel style={{ marginTop: "1.5rem", maxWidth: "700px" }}>
    <div
      onClick={onToggle}
      style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", userSelect: "none" }}
    >
      <h3 style={{ margin: 0 }}>{title}</h3>
      {isOpen ? <ChevronUp size={20} color="#888" /> : <ChevronDown size={20} color="#888" />}
    </div>
    <div style={{ overflow: "hidden", maxHeight: isOpen ? "4000px" : "0", transition: "max-height 0.35s ease" }}>
      <div style={{ marginTop: "0.85rem" }}>
        {children}
      </div>
    </div>
  </Panel>
);

const DEFAULT_PRICES = { DEF: 5.0, MID: 5.5, FWD: 6.0 };
const RATING_ADJ = { 1: -1.0, 2: -0.5, 3: 0.0, 4: 0.5, 5: 1.0 };
const computePrice = (pos, rating) =>
  Number(((DEFAULT_PRICES[pos] ?? 0) + (RATING_ADJ[rating] ?? 0)).toFixed(1));

const playersTableStyle = `
  .players-tight-table {
    width: auto !important;
    max-width: none !important;
    min-width: 0 !important;
  }
  .players-tight-table th,
  .players-tight-table td {
    width: auto !important;
  }
  @keyframes savePulse {
    0%   { box-shadow: 0 0 0 0 rgba(255, 105, 180, 0.55); }
    70%  { box-shadow: 0 0 0 6px rgba(255, 105, 180, 0); }
    100% { box-shadow: 0 0 0 0 rgba(255, 105, 180, 0); }
  }
  .save-pulse {
    animation: savePulse 1.3s ease-in-out infinite;
  }
`;

const ManageLeague = () => {
  const {leagueID } = useParams();
  const navigate = useNavigate();
  const [league, setLeague] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningAdvance, setRunningAdvance] = useState(false);
  const [currentPhase, setCurrentPhase] = useState(null);
  const [currentGameweekID, setCurrentGameweekID] = useState(null);
  const [showAdvancePrompt, setShowAdvancePrompt] = useState(false);
  const [currentDeadline, setCurrentDeadline] = useState(null);
  const [hasMatchResults, setHasMatchResults] = useState(false);
  const [showMatchSheet, setShowMatchSheet] = useState(false);
  const [matchList, setMatchList] = useState([]);
  const [hasPlayerPoints, setHasPlayerPoints] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPosition, setNewPosition] = useState("");
  const [newRating, setNewRating] = useState("");
  const [addingPlayer, setAddingPlayer] = useState(false);
  const [userDoc, setUserDoc] = useState(null);
  const [leagueUsers, setLeagueUsers] = useState([]);
  const [loadingLeagueUsers, setLoadingLeagueUsers] = useState(false);
  const [updatingRoleUserID, setUpdatingRoleUserID] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    message: "",
    onConfirm: null,
  });
  const [pendingPlayerPositions, setPendingPlayerPositions] = useState({});
  const [updatingPlayerPositionID, setUpdatingPlayerPositionID] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  const { showToast } = useToast();

  // Collapsible section state — all closed by default
  const [openSections, setOpenSections] = useState({ members: false, players: false, endSeason: false, availability: false, matchResults: false });
  const [availabilityResponses, setAvailabilityResponses] = useState({});
  const [availabilityEnabled, setAvailabilityEnabled] = useState(true);
  const [savingAvailability, setSavingAvailability] = useState({});
  const [savingAvailabilityToggle, setSavingAvailabilityToggle] = useState(false);
  const [savingPlayerLink, setSavingPlayerLink] = useState({});
  const [unlinkedAvailability, setUnlinkedAvailability] = useState({});
  const [savingUnlinked, setSavingUnlinked] = useState({});

  // Match Results (historic, all seasons/gameweeks) — collapsible section state
  const [matchResultsData, setMatchResultsData] = useState([]); // flat list: [{ key, seasonID, gwID, matchID, matchLabel, opponent, yourScore, theirScore, result }]
  const [loadingMatchResults, setLoadingMatchResults] = useState(false);
  const [pendingMatchEdits, setPendingMatchEdits] = useState({}); // { [key]: { opponent?, yourScore?, theirScore? } }
  const [savingMatchKey, setSavingMatchKey] = useState(null);
  const [savingAllMatches, setSavingAllMatches] = useState(false);
  const [openMatchSeasons, setOpenMatchSeasons] = useState({}); // { [seasonID]: bool } — most recent season defaults open, rest collapsed

  const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  const openConfirmModal = (message, onConfirm) => {
    setConfirmModal({ open: true, message, onConfirm });
  };

  const closeConfirmModal = () => {
    setConfirmModal({ open: false, message: "", onConfirm: null });
  };

  const handleConfirm = () => {
    const action = confirmModal.onConfirm;
    closeConfirmModal();
    if (action) action();
  };

  const handleAddPlayer = async () => {
    if (!newName || !newPosition || !newRating) {
      showToast("Please fill all fields", "error");
      return;
    }
    setAddingPlayer(true);
    try {
      const basePrice = computePrice(newPosition, newRating);
      const playersCol = paths.leaguePlayers(leagueID);
      const gwSnap = await getDocs(
        query(paths.leagueGameweeks(leagueID), where("isCurrent", "==", true))
      );
      const currentGW = !gwSnap.empty ? gwSnap.docs[0].id : "GW1";
      const pointHistory = { [currentGW]: 0 };
      const priceHistory = { [currentGW]: basePrice };
      const docRef = doc(playersCol);
      await setDoc(docRef, {
        id: docRef.id,
        name: newName,
        position: newPosition,
        rating: newRating,
        price: basePrice,
        currentPrice: basePrice,
        originalPrice: basePrice,
        joinedGW: currentGW,
        createdAt: new Date(),
        pointHistory,
        priceHistory,
        priceAccumulator: 0,
        priceDelta: 0,
      });
      showToast("Player added!", "success");
      const snap = await getDocs(query(paths.leaguePlayers(leagueID)));
      const playersList = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setPlayers(playersList);
      setNewName("");
      setNewPosition("");
      setNewRating("");
    } catch (err) {
      console.error("Error adding player:", err);
      showToast("Error adding player", "error");
    } finally {
      setAddingPlayer(false);
    }
  };

  const handleUpdatePlayerPosition = async (playerID, { silent = false } = {}) => {
    const player = players.find((p) => p.id === playerID);
    const nextPosition = pendingPlayerPositions[playerID] || player?.position;

    if (!player || !nextPosition || nextPosition === player.position) {
      return;
    }

    setUpdatingPlayerPositionID(playerID);
    try {
      await updateDoc(doc(paths.leaguePlayers(leagueID), playerID), {
        position: nextPosition,
      });

      setPlayers((prev) =>
        prev.map((p) =>
          p.id === playerID ? { ...p, position: nextPosition } : p
        )
      );

      setPendingPlayerPositions((prev) => {
        const next = { ...prev };
        delete next[playerID];
        return next;
      });

      if (!silent) showToast("Player position updated!", "success");
    } catch (err) {
      console.error("Error updating player position:", err);
      if (!silent) showToast("Error updating player position", "error");
    } finally {
      setUpdatingPlayerPositionID(null);
    }
  };

  const handleSaveAll = async () => {
    const ids = Object.keys(pendingPlayerPositions);
    if (ids.length === 0) return;
    setSavingAll(true);
    try {
      await Promise.all(ids.map((id) => handleUpdatePlayerPosition(id, { silent: true })));
      showToast("All changes saved", "success");
    } catch (err) {
      showToast("Some changes failed to save", "error");
    } finally {
      setSavingAll(false);
    }
  };

  // ---- Match Results (historic) helpers ----
  const getMatchFieldValue = (m, field) => {
    const pending = pendingMatchEdits[m.key];
    if (pending && Object.prototype.hasOwnProperty.call(pending, field)) return pending[field];
    return m[field];
  };

  const setMatchField = (m, field, value) => {
    setPendingMatchEdits((prev) => ({
      ...prev,
      [m.key]: { ...(prev[m.key] || {}), [field]: value },
    }));
  };

  const matchHasChanged = (m) => {
    const pending = pendingMatchEdits[m.key];
    if (!pending) return false;
    return ["opponent", "yourScore", "theirScore"].some(
      (f) => Object.prototype.hasOwnProperty.call(pending, f) && String(pending[f] ?? "") !== String(m[f] ?? "")
    );
  };

  const handleSaveMatchResult = async (m, { silent = false } = {}) => {
    if (!matchHasChanged(m)) return;
    setSavingMatchKey(m.key);
    try {
      const opponentVal = getMatchFieldValue(m, "opponent");
      const yourScoreVal = getMatchFieldValue(m, "yourScore");
      const theirScoreVal = getMatchFieldValue(m, "theirScore");

      const opponentTrimmed = typeof opponentVal === "string" ? opponentVal.trim() : "";
      const yourScoreNum =
        yourScoreVal !== "" && yourScoreVal !== undefined && yourScoreVal !== null ? Number(yourScoreVal) : null;
      const theirScoreNum =
        theirScoreVal !== "" && theirScoreVal !== undefined && theirScoreVal !== null ? Number(theirScoreVal) : null;
      const yourScoreValid = yourScoreNum !== null && !Number.isNaN(yourScoreNum);
      const theirScoreValid = theirScoreNum !== null && !Number.isNaN(theirScoreNum);
      const computedResult = computeMatchResult(
        yourScoreValid ? yourScoreNum : "",
        theirScoreValid ? theirScoreNum : ""
      );

      // Match fields stay optional here too — clearing a field removes it from
      // Firestore (via deleteField) rather than writing an empty string/zero.
      const updatePayload = {
        opponent: opponentTrimmed !== "" ? opponentTrimmed : deleteField(),
        yourScore: yourScoreValid ? yourScoreNum : deleteField(),
        theirScore: theirScoreValid ? theirScoreNum : deleteField(),
        result: computedResult || deleteField(),
      };

      await updateDoc(doc(paths.gwMatches(leagueID, m.gwID, m.seasonID), m.matchID), updatePayload);

      setMatchResultsData((prev) =>
        prev.map((row) =>
          row.key === m.key
            ? {
                ...row,
                opponent: opponentTrimmed,
                yourScore: yourScoreValid ? yourScoreNum : "",
                theirScore: theirScoreValid ? theirScoreNum : "",
                result: computedResult,
              }
            : row
        )
      );
      setPendingMatchEdits((prev) => {
        const next = { ...prev };
        delete next[m.key];
        return next;
      });

      if (!silent) showToast("Match result updated!", "success");
    } catch (err) {
      console.error("Error saving match result:", err);
      if (!silent) showToast("Error saving match result", "error");
    } finally {
      setSavingMatchKey(null);
    }
  };

  const handleSaveAllMatchResults = async () => {
    const keys = Object.keys(pendingMatchEdits);
    if (keys.length === 0) return;
    setSavingAllMatches(true);
    try {
      const rows = matchResultsData.filter((m) => keys.includes(m.key) && matchHasChanged(m));
      await Promise.all(rows.map((m) => handleSaveMatchResult(m, { silent: true })));
      showToast("All match results saved", "success");
    } catch (err) {
      showToast("Some match results failed to save", "error");
    } finally {
      setSavingAllMatches(false);
    }
  };

  const handleAdminSetAvailability = async (userUID, value) => {
    if (!leagueID || !currentGameweekID || !league?.currentSeasonID) return;
    setSavingAvailability(prev => ({ ...prev, [userUID]: true }));
    try {
      const availRef = doc(
        db,
        "leagues", leagueID,
        "seasons", league.currentSeasonID,
        "gameweeks", currentGameweekID,
        "availability", "responses"
      );
      try {
        await updateDoc(availRef, { [`responses.${userUID}`]: value });
      } catch (err) {
        if (err.code === "not-found") {
          await setDoc(availRef, { responses: { [userUID]: value } }, { merge: true });
        } else throw err;
      }
      setAvailabilityResponses(prev => ({ ...prev, [userUID]: value }));
    } catch (err) {
      console.error("Error updating availability:", err);
      showToast("Error updating availability", "error");
    } finally {
      setSavingAvailability(prev => ({ ...prev, [userUID]: false }));
    }
  };

  const handleToggleAvailabilityEnabled = async () => {
    setSavingAvailabilityToggle(true);
    try {
      const next = !availabilityEnabled;
      await updateDoc(paths.league(leagueID), { availabilityEnabled: next });
      setAvailabilityEnabled(next);
      showToast(next ? "Availability notifications enabled" : "Availability notifications disabled", "success");
    } catch (err) {
      showToast("Error updating setting", "error");
    } finally {
      setSavingAvailabilityToggle(false);
    }
  };

  const handleAdminLinkPlayer = async (targetUID, playerID) => {
    if (!leagueID || !targetUID) return;
    setSavingPlayerLink(prev => ({ ...prev, [targetUID]: true }));
    try {
      await updateDoc(doc(db, "users", targetUID), {
        [`linkedPlayerIDs.${leagueID}`]: playerID || "",
      });
      setLeagueUsers(prev => prev.map(u =>
        u.id === targetUID
          ? { ...u, linkedPlayerIDs: { ...(u.linkedPlayerIDs || {}), [leagueID]: playerID } }
          : u
      ));
      showToast("Player linked!", "success");
    } catch (err) {
      console.error("Error linking player:", err);
      showToast("Error linking player", "error");
    } finally {
      setSavingPlayerLink(prev => ({ ...prev, [targetUID]: false }));
    }
  };

  const handleUnlinkedPlayerAvailability = async (playerID, value) => {
    if (!leagueID || !currentGameweekID || !league?.currentSeasonID) return;
    setSavingUnlinked(prev => ({ ...prev, [playerID]: true }));
    try {
      const availRef = doc(
        db,
        "leagues", leagueID,
        "seasons", league.currentSeasonID,
        "gameweeks", currentGameweekID,
        "availability", "responses"
      );
      const newValue = unlinkedAvailability[playerID] === value ? null : value;
      try {
        if (newValue === null) {
          await updateDoc(availRef, { [`responses.${playerID}`]: deleteField() });
        } else {
          await updateDoc(availRef, { [`responses.${playerID}`]: newValue });
        }
      } catch (err) {
        if (err.code === "not-found") {
          await setDoc(availRef, { responses: { [playerID]: value } }, { merge: true });
        } else throw err;
      }
      setUnlinkedAvailability(prev => ({ ...prev, [playerID]: newValue }));
    } catch (err) {
      console.error("Error updating unlinked player availability:", err);
      showToast("Error updating availability", "error");
    } finally {
      setSavingUnlinked(prev => ({ ...prev, [playerID]: false }));
    }
  };

  const fetchLeague = async () => {
    try {
      const leagueDoc = await getDoc(paths.league(leagueID));
      if (leagueDoc.exists()) {
        setLeague({ id: leagueDoc.id, ...leagueDoc.data() });
        if (leagueDoc.data().currentSeasonID) {
          setActiveSeasonID(leagueDoc.data().currentSeasonID);
        }
      }

      const snap = await getDocs(
        query(paths.leaguePlayers(leagueID))
      );
      const playersList = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setPlayers(playersList);

      const gwSnap = await getDocs(
        query(paths.leagueGameweeks(leagueID), where("isCurrent", "==", true))
      );
      if (!gwSnap.empty) {
        const gwDoc = gwSnap.docs[0];
        setCurrentGameweekID(gwDoc.id);
        const gwData = gwDoc.data();
        setCurrentPhase(gwData.phase ? gwData.phase.trim().toLowerCase() : null);
        setCurrentDeadline(gwData.deadline || null);
        if (
          gwData.phase === "pick" &&
          gwData.deadline &&
          (gwData.deadline.toDate ? gwData.deadline.toDate() : new Date(gwData.deadline)) < new Date()
        ) {
          await updateDoc(paths.gameweek(leagueID, gwDoc.id), { phase: "results" });
          setCurrentPhase("results");
        }
        if (
          gwData.phase === "results" &&
          gwData.deadline &&
          (gwData.deadline.toDate ? gwData.deadline.toDate() : new Date(gwData.deadline)) > new Date()
        ) {
          await updateDoc(paths.gameweek(leagueID, gwDoc.id), { phase: "pick" });
          setCurrentPhase("pick");
        }
      } else {
        setCurrentGameweekID(null);
        setCurrentPhase(null);
        setCurrentDeadline(null);
      }
      // Fetch availabilityEnabled from league doc
      if (leagueDoc.exists()) {
        setAvailabilityEnabled(leagueDoc.data().availabilityEnabled !== false);
      }

      // Fetch availability responses for current GW
      const currentGWID = gwSnap.empty ? null : gwSnap.docs[0].id;
      if (currentGWID && leagueDoc.data().currentSeasonID) {
        try {
          const availRef = doc(
            db,
            "leagues", leagueID,
            "seasons", leagueDoc.data().currentSeasonID,
            "gameweeks", currentGWID,
            "availability", "responses"
          );
          const availSnap = await getDoc(availRef);
          setAvailabilityResponses(availSnap.exists() ? (availSnap.data()?.responses || {}) : {});
        } catch (_) {}
      }
    } catch (error) {
      console.error("Error fetching league:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeague();
    // eslint-disable-next-line
  }, [leagueID]);

  useEffect(() => {
    const fetchUserDoc = async () => {
      try {
        const auth = getAuth();
        const u = auth.currentUser;
        if (!u?.uid) { setUserDoc(null); return; }
        const snap = await getDoc(ref("users", u.uid));
        setUserDoc(snap.exists() ? snap.data() : null);
      } catch (e) {
        setUserDoc(null);
      }
    };
    fetchUserDoc();
    // eslint-disable-next-line
  }, [leagueID]);

  useEffect(() => {
    const fetchLeagueUsers = async () => {
      if (!leagueID) { setLeagueUsers([]); return; }
      setLoadingLeagueUsers(true);
      try {
        const snap = await getDocs(
          query(col("users"), where("leagueIDs", "array-contains", leagueID))
        );
        const users = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const aName = (a.displayName || a.email || "").toLowerCase();
            const bName = (b.displayName || b.email || "").toLowerCase();
            return aName.localeCompare(bName);
          });
        setLeagueUsers(users);
      } catch (e) {
        console.warn("Failed to fetch league users", e);
        setLeagueUsers([]);
      } finally {
        setLoadingLeagueUsers(false);
      }
    };
    fetchLeagueUsers();
  }, [leagueID]);

  useEffect(() => {
    const checkMatchResults = async () => {
      if (!leagueID || !currentGameweekID) {
        setHasMatchResults(false);
        setMatchList([]);
        return;
      }
      try {
        const matchesSnap = await getDocs(paths.gwMatches(leagueID, currentGameweekID));
        setHasMatchResults(!matchesSnap.empty);
        const matches = matchesSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const aTime = a.createdAt?.toMillis?.() ?? 0;
            const bTime = b.createdAt?.toMillis?.() ?? 0;
            return aTime - bTime;
          });
        setMatchList(
          matches.map((m, idx) => ({ id: m.id, label: `Match ${idx + 1}` }))
        );
        if (!matchesSnap.empty && currentPhase === "results") {
          setShowAdvancePrompt(true);
        }
      } catch (err) {
        setHasMatchResults(false);
        setMatchList([]);
      }
    };
    checkMatchResults();
  }, [leagueID, currentGameweekID, currentPhase]);

  // Historic Match Results — every season, every gameweek that already has a
  // match document on file. Skips gameweeks with no match doc at all (nothing
  // to edit there); only surfaces entries that genuinely exist in Firestore.
  useEffect(() => {
    if (!leagueID) return;
    let cancelled = false;
    (async () => {
      setLoadingMatchResults(true);
      try {
        const seasonsSnap = await getDocs(collection(db, "leagues", leagueID, "seasons"));
        const seasonIDs = seasonsSnap.docs
          .map((d) => d.id)
          .filter((id) => /^S\d+$/.test(id))
          .sort((a, b) => (parseInt(b.replace("S", ""), 10) || 0) - (parseInt(a.replace("S", ""), 10) || 0)); // most recent season first

        const allMatches = [];
        for (const seasonID of seasonIDs) {
          const gwSnap = await getDocs(paths.leagueGameweeks(leagueID, seasonID));
          const gwIDs = gwSnap.docs
            .map((d) => d.id)
            .filter((id) => id !== "meta")
            .sort((a, b) => (parseInt(String(b).replace(/\D/g, ""), 10) || 0) - (parseInt(String(a).replace(/\D/g, ""), 10) || 0)); // most recent GW first

          for (const gwID of gwIDs) {
            const matchSnap = await getDocs(paths.gwMatches(leagueID, gwID, seasonID));
            if (matchSnap.empty) continue;
            const matches = matchSnap.docs
              .map((d) => ({ id: d.id, ...d.data() }))
              .sort((a, b) => (a.createdAt?.toMillis?.() ?? 0) - (b.createdAt?.toMillis?.() ?? 0));
            matches.forEach((m, idx) => {
              allMatches.push({
                key: `${seasonID}__${gwID}__${m.id}`,
                seasonID,
                gwID,
                matchID: m.id,
                matchLabel: `Match ${idx + 1}`,
                opponent: m.opponent || "",
                yourScore: m.yourScore ?? "",
                theirScore: m.theirScore ?? "",
                result: m.result || null,
              });
            });
          }
        }

        if (!cancelled) setMatchResultsData(allMatches);
      } catch (err) {
        console.error("Error loading match results history:", err);
        if (!cancelled) setMatchResultsData([]);
      } finally {
        if (!cancelled) setLoadingMatchResults(false);
      }
    })();
    return () => { cancelled = true; };
  }, [leagueID]);

  useEffect(() => {
    const checkPlayerPoints = async () => {
      if (!leagueID || !currentGameweekID) { setHasPlayerPoints(false); return; }
      try {
        const playerPointsSnap = await getDocs(paths.gwPlayerPoints(leagueID, currentGameweekID));
        setHasPlayerPoints(!playerPointsSnap.empty);
      } catch (err) {
        setHasPlayerPoints(false);
      }
    };
    checkPlayerPoints();
  }, [leagueID, currentGameweekID]);

  const runConfirmedAdvance = async () => {
    if (!leagueID || !currentGameweekID || !league) return;

    setRunningAdvance(true);
    try {
      await updateDoc(paths.gameweek(leagueID, currentGameweekID), { phase: "scoring" });
      setCurrentPhase("scoring");

      const matchesSnap = await getDocs(paths.gwMatches(leagueID, currentGameweekID));
      const matches = matchesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const playerPoints = await calculatePointsFromMatches(
        String(leagueID),
        String(currentGameweekID),
        matches
      );

      const teamsSnap = await getDocs(paths.gwTeams(leagueID, currentGameweekID));
      const teams = teamsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      teams.forEach((team) => {
        calculateTeamPoints(team, playerPoints, currentGameweekID);
      });

      setHasPlayerPoints(true);
      showToast("Scoring complete. Advancing gameweek...", "success");

      const validLeagueID = String(leagueID);
      const auth = getAuth();
      const u = auth.currentUser;

      const actor = {
        userID: u?.uid || "system",
        userName: userDoc?.displayName || u?.displayName || u?.email || "",
        role: (userDoc?.roles && leagueID ? userDoc.roles[leagueID] : "admin") || "admin",
        source: "manage_league",
      };

      await advanceGameweek(validLeagueID, actor);

      setShowAdvancePrompt(false);
      await fetchLeague();
      showToast("Gameweek advanced!", "success");
    } catch (error) {
      console.error("Error scoring/advancing gameweek:", error);
      await logError({
        leagueID,
        userID: "admin",
        action: "ManageLeague_scoringAndAdvanceGameweek",
        payload: { currentGameweekID, currentPhase },
        error,
      });
      showToast("Error advancing gameweek. Check console.", "error");
      try {
        await updateDoc(paths.gameweek(leagueID, currentGameweekID), { phase: "results" });
        setCurrentPhase("results");
      } catch (e) {}
    } finally {
      setRunningAdvance(false);
    }
  };

  const scoringAndAdvanceGameweek = async () => {
    if (!leagueID || !currentGameweekID || !league) return;
    openConfirmModal(
      "This will lock results, calculate points, update prices, and advance to the next gameweek. Continue?",
      () => { void runConfirmedAdvance(); }
    );
  };

  const matchDayOptions = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  const [deadlineMode, setDeadlineMode] = useState("preset");
  const [matchDay, setMatchDay] = useState("");
  const [kickoffTime, setKickoffTime] = useState("");

  const [customDate, setCustomDate] = useState("");
  const [customTime, setCustomTime] = useState("");
  const [customSaveAsDefaultTime, setCustomSaveAsDefaultTime] = useState(false);

  const kickoffTimeOptions = (() => {
    const opts = [];
    for (let h = 6; h <= 23; h++) {
      for (let m of [0, 30]) {
        const hh = String(h).padStart(2, "0");
        const mm = String(m).padStart(2, "0");
        const value = `${hh}:${mm}`;
        const hour12 = ((h + 11) % 12) + 1;
        const ampm = h < 12 ? "AM" : "PM";
        const label = `${hour12}:${mm} ${ampm}`;
        opts.push({ value, label });
      }
    }
    return opts;
  })();

  useEffect(() => {
    if (!league) return;
    if (!matchDay) setMatchDay(league.matchDay || "");
    if (!kickoffTime) {
      if (league.defaultKickoffTime) {
        setKickoffTime(league.defaultKickoffTime);
      } else if (league.timeOfDay) {
        const t = String(league.timeOfDay).toLowerCase();
        if (t === "evening") setKickoffTime("14:00");
        else setKickoffTime("09:00");
      } else {
        setKickoffTime("09:00");
      }
    }
    if (!customDate || !customTime) {
      const d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      if (!customDate) setCustomDate(`${yyyy}-${mm}-${dd}`);
      if (!customTime) setCustomTime(kickoffTime || "09:00");
    }
    if (league.defaultDeadlineMode && (league.defaultDeadlineMode === "preset" || league.defaultDeadlineMode === "custom")) {
      setDeadlineMode(league.defaultDeadlineMode);
    }
  }, [league]);

  function getNextDateForDay(dayName) {
    const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    const now = new Date();
    const todayIdx = now.getDay();
    const targetIdx = daysOfWeek.indexOf(dayName);
    const todayIdxForDaysOfWeek = todayIdx === 0 ? 6 : todayIdx - 1;
    let daysToAdd = (targetIdx - todayIdxForDaysOfWeek + 7) % 7;
    const result = new Date(now);
    result.setDate(now.getDate() + daysToAdd);
    return result;
  }

  function computeDeadlineFromMatchDayAndKickoff(matchDay, kickoffTime) {
    const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    if (!daysOfWeek.includes(matchDay)) return null;

    let matchDate = getNextDateForDay(matchDay);

    const [hhRaw, mmRaw] = String(kickoffTime || "09:00").split(":");
    const hh = Number(hhRaw);
    const mm = Number(mmRaw);
    if (Number.isFinite(hh) && Number.isFinite(mm)) {
      matchDate.setHours(hh, mm, 0, 0);
    } else {
      matchDate.setHours(9, 0, 0, 0);
    }

    return {
      timestamp: matchDate,
      formatted: matchDate.toLocaleString("en-US", {
        month: "long", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit",
      }),
    };
  }

  function formatDeadlineForDisplay(d) {
    if (!d) return "Not set";
    const dt = d?.toDate ? d.toDate() : new Date(d);
    if (!(dt instanceof Date) || isNaN(dt)) return "Not set";
    return dt.toLocaleString("en-US", {
      weekday: "long", month: "long", day: "numeric",
      year: "numeric", hour: "numeric", minute: "2-digit",
    });
  }

  function safeDateFromCustom(customDate, customTime) {
    if (!customDate || !customTime) return null;
    const [y, mo, d] = String(customDate).split("-").map((x) => Number(x));
    const [hh, mm] = String(customTime).split(":").map((x) => Number(x));
    const dt = new Date(y, (mo || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0);
    return dt instanceof Date && !isNaN(dt) ? dt : null;
  }

  async function handleApplyMatchDayTime({ alsoSetLeague = false } = {}) {
    if (!currentGameweekID) { showToast("No current gameweek set.", "error"); return; }
    if (!matchDay || !kickoffTime) { showToast("Please select Match Day and a kickoff time.", "error"); return; }
    try {
      const { timestamp, formatted } = computeDeadlineFromMatchDayAndKickoff(matchDay, kickoffTime);
      const gwMatch = String(currentGameweekID || "").match(/^GW(\d+)$/i);
      if (gwMatch) {
        const gwNum = Number(gwMatch[1]);
        if (Number.isFinite(gwNum) && gwNum > 1) {
          const prevGWID = `GW${gwNum - 1}`;
          const prevSnap = await getDoc(paths.gameweek(leagueID, prevGWID));
          if (prevSnap.exists()) {
            const prevData = prevSnap.data();
            const prevDeadlineRaw = prevData?.deadline;
            if (prevDeadlineRaw) {
              const prevDeadline = prevDeadlineRaw.toDate ? prevDeadlineRaw.toDate() : new Date(prevDeadlineRaw);
              const nextDeadline = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
              if (prevDeadline instanceof Date && !isNaN(prevDeadline) && nextDeadline instanceof Date && !isNaN(nextDeadline)) {
                if (nextDeadline <= prevDeadline) {
                  showToast("Deadline must be after the previous gameweek's deadline.", "error");
                  return;
                }
              }
            }
          }
        }
      }
      await updateDoc(paths.gameweek(leagueID, currentGameweekID), { matchDay, kickoffTime, deadline: timestamp });
      if (timestamp > new Date()) {
        await updateDoc(paths.gameweek(leagueID, currentGameweekID), { phase: "pick" });
        setCurrentPhase("pick");
      } else if (timestamp < new Date()) {
        await updateDoc(paths.gameweek(leagueID, currentGameweekID), { phase: "results" });
        setCurrentPhase("results");
      }
      setCurrentDeadline(timestamp);
      const yyyy = timestamp.getFullYear();
      const mm = String(timestamp.getMonth() + 1).padStart(2, "0");
      const dd = String(timestamp.getDate()).padStart(2, "0");
      const hh = String(timestamp.getHours()).padStart(2, "0");
      const mi = String(timestamp.getMinutes()).padStart(2, "0");
      setCustomDate(`${yyyy}-${mm}-${dd}`);
      setCustomTime(`${hh}:${mi}`);
      setLeague(prev => prev ? { ...prev, matchDay, defaultKickoffTime: kickoffTime } : prev);
      if (alsoSetLeague) {
        await updateDoc(paths.league(leagueID), { matchDay, defaultKickoffTime: kickoffTime, defaultDeadlineMode: "preset" });
        setLeague(l => l ? { ...l, matchDay, defaultKickoffTime: kickoffTime, defaultDeadlineMode: "preset" } : l);
      }
      showToast(
        alsoSetLeague
          ? "Match Day and Kickoff Time updated for current gameweek and set as league default."
          : "Match Day and Kickoff Time updated for current gameweek.",
        "success"
      );
    } catch (err) {
      console.error("Error updating match day/time:", err);
      await logError({ leagueID, userID: "admin", action: "ManageLeague_handleApplyMatchDayTime", payload: { matchDay, kickoffTime }, error: err });
      showToast("Error updating match day/time.", "error");
    }
  }

  async function handleApplyCustomDeadline({ alsoSetLeague = false } = {}) {
    if (!currentGameweekID) { showToast("No current gameweek set.", "error"); return; }
    if (!customDate || !customTime) { showToast("Please select a custom deadline date and time.", "error"); return; }
    try {
      const [y, mo, d] = customDate.split("-").map((x) => Number(x));
      const [hh, mm] = customTime.split(":").map((x) => Number(x));
      const dt = new Date(y, (mo || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0);

      const gwMatch = String(currentGameweekID || "").match(/^GW(\d+)$/i);
      if (gwMatch) {
        const gwNum = Number(gwMatch[1]);
        if (Number.isFinite(gwNum) && gwNum > 1) {
          const prevGWID = `GW${gwNum - 1}`;
          const prevSnap = await getDoc(paths.gameweek(leagueID, prevGWID));
          if (prevSnap.exists()) {
            const prevData = prevSnap.data();
            const prevDeadlineRaw = prevData?.deadline;
            if (prevDeadlineRaw) {
              const prevDeadline = prevDeadlineRaw.toDate ? prevDeadlineRaw.toDate() : new Date(prevDeadlineRaw);
              if (prevDeadline instanceof Date && !isNaN(prevDeadline)) {
                if (dt <= prevDeadline) {
                  showToast("Deadline must be after the previous gameweek's deadline.", "error");
                  return;
                }
              }
            }
          }
        }
      }

      await updateDoc(paths.gameweek(leagueID, currentGameweekID), { deadline: dt, deadlineSource: "custom" });

      if (dt > new Date()) {
        await updateDoc(paths.gameweek(leagueID, currentGameweekID), { phase: "pick" });
        setCurrentPhase("pick");
      } else {
        await updateDoc(paths.gameweek(leagueID, currentGameweekID), { phase: "results" });
        setCurrentPhase("results");
      }

      setCurrentDeadline(dt);
      const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      setMatchDay(daysOfWeek[dt.getDay()] || "");
      const hh2 = String(dt.getHours()).padStart(2, "0");
      const mi2 = String(dt.getMinutes()).padStart(2, "0");
      setKickoffTime(`${hh2}:${mi2}`);

      if (alsoSetLeague) {
        const next = { defaultDeadlineMode: "custom" };
        if (customSaveAsDefaultTime) next.defaultKickoffTime = customTime;
        await updateDoc(paths.league(leagueID), next);
        setLeague((l) => (l ? { ...l, ...next } : l));
      }

      showToast(
        alsoSetLeague
          ? "Custom deadline set for this gameweek (and saved as default settings)."
          : "Custom deadline set for this gameweek.",
        "success"
      );
    } catch (err) {
      console.error("Error setting custom deadline:", err);
      await logError({ leagueID, userID: "admin", action: "ManageLeague_handleApplyCustomDeadline", payload: { customDate, customTime, currentGameweekID }, error: err });
      showToast("Error setting custom deadline.", "error");
    }
  }

  async function handleAddWeekToDeadline() {
    if (!currentGameweekID || !currentDeadline) {
      showToast("No current gameweek or deadline found.", "error");
      return;
    }
    try {
      const oldDate = currentDeadline.toDate?.() ?? new Date(currentDeadline);
      const newDate = new Date(oldDate);
      newDate.setDate(newDate.getDate() + 7);

      await updateDoc(paths.gameweek(leagueID, currentGameweekID), { deadline: newDate });

      if (newDate > new Date()) {
        await updateDoc(paths.gameweek(leagueID, currentGameweekID), { phase: "pick" });
        setCurrentPhase("pick");
      } else {
        await updateDoc(paths.gameweek(leagueID, currentGameweekID), { phase: "results" });
        setCurrentPhase("results");
      }

      setCurrentDeadline(newDate);
      showToast("✅ Deadline extended by 1 week.", "success");
    } catch (err) {
      console.error("Error extending deadline:", err);
      await logError({ leagueID, userID: "admin", action: "ManageLeague_handleAddWeekToDeadline", payload: { currentGameweekID, currentDeadline }, error: err });
      showToast("Error extending deadline. Check console.", "error");
    }
  }

  if (loading) {
    return <PageLoader message="Loading league…" />;
  }

  if (!league) {
    return <p>No league found.</p>;
  }

  const getUserRoleForLeague = (u) => {
    const roles = u?.roles && typeof u.roles === "object" ? u.roles : {};
    return roles?.[leagueID] || "user";
  };
  const adminCount = leagueUsers.filter((u) => getUserRoleForLeague(u) === "admin").length;

  const performUserRoleUpdate = async (targetUserID, nextRole) => {
    setUpdatingRoleUserID(targetUserID);
    try {
      const rolesPath = `roles.${leagueID}`;
      if (nextRole === "user") {
        await updateDoc(ref("users", targetUserID), { [rolesPath]: deleteField() });
      } else {
        await updateDoc(ref("users", targetUserID), { [rolesPath]: nextRole });
      }
      setLeagueUsers((prev) =>
        prev.map((u) =>
          u.id === targetUserID
            ? { ...u, roles: nextRole === "user" ? { ...(u.roles || {}) } : { ...(u.roles || {}), [leagueID]: nextRole } }
            : u
        )
      );
      if (nextRole === "user") {
        setLeagueUsers((prev) =>
          prev.map((u) => {
            if (u.id !== targetUserID) return u;
            const nextRoles = { ...(u.roles || {}) };
            delete nextRoles[leagueID];
            return { ...u, roles: nextRoles };
          })
        );
      }
      showToast("Role updated.", "success");
    } catch (e) {
      console.error("Failed to update user role", e);
      await logError({
        leagueID,
        userID: getAuth().currentUser?.uid || "unknown",
        action: "ManageLeague_updateUserRoleForLeague",
        payload: { targetUserID, nextRole },
        error: e,
      });
      showToast("Error updating role. Check console.", "error");
    } finally {
      setUpdatingRoleUserID(null);
    }
  };

  const updateUserRoleForLeague = async (targetUserID, nextRole) => {
    if (!leagueID || !targetUserID) return;
    const auth = getAuth();
    const currentUID = auth.currentUser?.uid;
    const targetUser = leagueUsers.find((u) => u.id === targetUserID);
    const targetCurrentRole = targetUser ? getUserRoleForLeague(targetUser) : "user";
    if (targetCurrentRole === "admin" && nextRole !== "admin" && adminCount <= 1) {
      showToast("This league must always have at least one admin.", "error");
      return;
    }
    if (currentUID && targetUserID === currentUID && nextRole !== "admin") {
      openConfirmModal(
        "You are about to remove your own admin access for this league. Continue?",
        () => { void performUserRoleUpdate(targetUserID, nextRole); }
      );
      return;
    }
    await performUserRoleUpdate(targetUserID, nextRole);
  };

  function getPhaseLabel(phase) {
    if (
      phase === "pick" &&
      currentDeadline &&
      (currentDeadline.toDate ? currentDeadline.toDate() : new Date(currentDeadline)) < new Date()
    ) {
      return <span style={{ color: "#ff9800", fontWeight: "bold", padding: "4px 0", fontSize: "1.05em", display: "inline-block" }}>Waiting for Results – Enter match data</span>;
    }
    if (phase === "pick") {
      return <span style={{ color: "#2ecc40", fontWeight: "bold", padding: "4px 0", fontSize: "1.05em", display: "inline-block" }}>Pick Phase – Selections Open</span>;
    }
    if (phase === "results") {
      return <span style={{ color: "#ff9800", fontWeight: "bold", padding: "4px 0", fontSize: "1.05em", display: "inline-block" }}>Results Phase – Enter Match Data</span>;
    }
    if (phase === "scoring") {
      return <span style={{ color: "#2196f3", fontWeight: "bold", padding: "4px 0", fontSize: "1.05em", display: "inline-block" }}>
        {hasPlayerPoints ? "Scoring Phase – Points calculated" : "Scoring Phase – Points calculating"}
      </span>;
    }
    if (phase === "closed") {
      return <span style={{ color: "#222", fontWeight: "bold", padding: "4px 0", fontSize: "1.05em", display: "inline-block" }}>Closed – Completed</span>;
    }
    return <span style={{ color: "#888", fontWeight: "bold", padding: "4px 0", fontSize: "1.05em", display: "inline-block" }}>Inactive / Setup</span>;
  }

  const lastAdvancedValue = league?.lastAdvancedAt || league?.lastAdvanced;

  return (
    <>
      <style>{playersTableStyle}</style>
      <LoadingOverlay show={runningAdvance} message="Advancing gameweek…" />

      {/* League info card */}
      <Panel style={{ marginBottom: "0.75rem", maxWidth: "700px" }}>
        <h2 style={{ marginTop: 0, marginBottom: "6px" }}>
          {league.name || leagueID}
        </h2>
        <div style={{ marginBottom: "14px" }}>
          {getPhaseLabel(currentPhase)}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "10px", marginBottom: "5px" }}>
          <div style={{ background: "#f6f6f6", padding: "10px 12px", borderRadius: "8px" }}>
            <div style={{ fontSize: "0.8rem", color: "#777" }}>Gameweek</div>
            <div style={{ fontWeight: 800 }}>{currentGameweekID || "—"}</div>
          </div>
          <div style={{ background: "#f6f6f6", padding: "10px 12px", borderRadius: "8px" }}>
            <div style={{ fontSize: "0.8rem", color: "#777" }}>Deadline</div>
            <div style={{ fontWeight: 800 }}>
              {currentDeadline
                ? (currentDeadline.toDate
                    ? currentDeadline.toDate().toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
                    : new Date(currentDeadline).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }))
                : "Not set"}
            </div>
          </div>
          <div style={{ background: "#f6f6f6", padding: "10px 12px", borderRadius: "8px" }}>
            <div style={{ fontSize: "0.8rem", color: "#777" }}>League Code</div>
            <div style={{ fontWeight: 800 }}>{league?.code || "—"}</div>
          </div>
          <div style={{ background: "#f6f6f6", padding: "10px 12px", borderRadius: "8px" }}>
            <div style={{ fontSize: "0.8rem", color: "#777" }}>Season</div>
            <div style={{ fontWeight: 800 }}>{league?.currentSeasonID || "—"}</div>
          </div>
        </div>
      </Panel>

      {/* Results Actions */}
      <div style={{ maxWidth: "700px" }}>
        {currentPhase === "results" && (
          <div style={{ fontWeight: 800, margin: "0.25rem 0 0.35rem 0", color: "#444" }}>
            Results Actions
          </div>
        )}
        <div style={{ display: "flex", gap: "10px", marginBottom: "1rem", position: "relative", marginTop: "0.4rem", flexWrap: "wrap" }}>
          {currentPhase === "results" && (
            <div
              style={{
                display: "inline-block",
                background: "#50B4C8",
                color: "#fff",
                borderRadius: "4px",
                fontWeight: 500,
                cursor: "pointer",
                padding: "8px 16px",
                userSelect: "none",
                minWidth: "155px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
                border: "none",
                outline: "none",
                fontSize: "1em",
              }}
              onClick={() => setShowMatchSheet(true)}
              tabIndex={0}
            >
              <span style={{ color: "#fff" }}>
                {hasMatchResults ? "Edit Match Results" : "Enter Match Results"}
              </span>
            </div>
          )}
          {currentPhase === "results" && hasMatchResults && (
            <Button
              onClick={() => navigate(`/matchinput/${leagueID}/${currentGameweekID}/new`)}
              disabled={!leagueID || !currentGameweekID}
            >
              Add Another Match
            </Button>
          )}
          {hasMatchResults && (
            <Button onClick={() => navigate(`/matches/${leagueID}/${currentGameweekID}`)}>
              View Match Results
            </Button>
          )}
          {currentPhase === "results" && (
            <Button
              onClick={scoringAndAdvanceGameweek}
              disabled={!hasMatchResults}
              variant="pink"
            >
              Advance Gameweek
            </Button>
          )}
        </div>
      </div>

      {/* Deadline Settings */}
      {(currentPhase !== "closed" && currentPhase !== "scoring" && !hasMatchResults)
        ? (
        <Panel style={{ marginTop: "1.5rem", maxWidth: "700px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
            <h3 style={{ margin: 0 }}>Deadline Settings</h3>
            <div style={{ display: "flex", gap: "8px" }}>
              <button type="button" onClick={() => setDeadlineMode("preset")} style={{ padding: "8px 14px", borderRadius: "999px", border: "1px solid #cfe9ef", background: deadlineMode === "preset" ? "#50B4C8" : "#f3f6f7", color: deadlineMode === "preset" ? "#fff" : "#2b2b2b", fontWeight: 800, cursor: "pointer" }}>
                Preset
              </button>
              <button type="button" onClick={() => setDeadlineMode("custom")} style={{ padding: "8px 14px", borderRadius: "999px", border: "1px solid #cfe9ef", background: deadlineMode === "custom" ? "#50B4C8" : "#f3f6f7", color: deadlineMode === "custom" ? "#fff" : "#2b2b2b", fontWeight: 800, cursor: "pointer" }}>
                Custom
              </button>
            </div>
          </div>
          <div style={{ marginTop: "0.85rem", marginBottom: "0.9rem", padding: "12px 14px", background: "#f6f6f6", border: "1px solid #e8e8e8", borderRadius: "8px" }}>
            <div style={{ color: "#666", fontSize: "0.92em", marginBottom: "4px" }}>New deadline will be:</div>
            <div style={{ fontWeight: 800, fontSize: "1.05em" }}>
              {deadlineMode === "preset"
                ? (() => {
                    if (!matchDay || !kickoffTime) return "—";
                    const res = computeDeadlineFromMatchDayAndKickoff(matchDay, kickoffTime);
                    return res?.timestamp ? formatDeadlineForDisplay(res.timestamp) : "—";
                  })()
                : (() => {
                    const dt = safeDateFromCustom(customDate, customTime);
                    return dt ? formatDeadlineForDisplay(dt) : "—";
                  })()
              }
            </div>
            <div style={{ marginTop: "6px", color: "#777", fontSize: "0.9em" }}>
              Current deadline: <strong>{formatDeadlineForDisplay(currentDeadline)}</strong>
            </div>
          </div>
          {deadlineMode === "preset" && (
            <div>
              <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", alignItems: "flex-end" }}>
                <div style={{ minWidth: "240px" }}>
                  <div style={{ fontWeight: 700, marginBottom: "6px" }}>Match Day</div>
                  <Dropdown value={matchDay} onChange={(opt) => setMatchDay(opt)} options={matchDayOptions.map(day => ({ value: day, label: day }))} placeholder="Select Match Day" menuPortalTarget={document.body} styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }} />
                </div>
                <div style={{ minWidth: "240px" }}>
                  <div style={{ fontWeight: 700, marginBottom: "6px" }}>Kickoff Time</div>
                  <Dropdown value={kickoffTime} onChange={(opt) => setKickoffTime(opt)} options={kickoffTimeOptions} placeholder="Select kickoff time" menuPortalTarget={document.body} styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }} />
                </div>
              </div>
              <div style={{ marginTop: "14px", display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
                <Button onClick={() => handleApplyMatchDayTime({ alsoSetLeague: false })} disabled={!matchDay || !kickoffTime} variant="pink">Apply to {currentGameweekID || "this gameweek"}</Button>
                <Button onClick={() => handleApplyMatchDayTime({ alsoSetLeague: true })} disabled={!matchDay || !kickoffTime}>Save default settings</Button>
                <button type="button" onClick={handleAddWeekToDeadline} disabled={!currentDeadline || !currentGameweekID} style={{ border: "none", background: "transparent", padding: 0, color: "#1976d2", cursor: !currentDeadline || !currentGameweekID ? "not-allowed" : "pointer", fontWeight: 700 }}>
                  + Add 1 week
                </button>
              </div>
            </div>
          )}
          {deadlineMode === "custom" && (
            <div>
              <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", alignItems: "flex-end" }}>
                <div style={{ minWidth: "240px" }}>
                  <div style={{ fontWeight: 700, marginBottom: "6px" }}>Date</div>
                  <input type="date" value={customDate} onChange={(e) => setCustomDate(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #ddd" }} />
                </div>
                <div style={{ minWidth: "240px" }}>
                  <div style={{ fontWeight: 700, marginBottom: "6px" }}>Time</div>
                  <input type="time" value={customTime} onChange={(e) => setCustomTime(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #ddd" }} />
                </div>
              </div>
              <div style={{ marginTop: "10px", color: "#666" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <input type="checkbox" checked={customSaveAsDefaultTime} onChange={(e) => setCustomSaveAsDefaultTime(e.target.checked)} />
                  Save as default kickoff time
                </label>
              </div>
              <div style={{ marginTop: "14px", display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
                <Button onClick={() => handleApplyCustomDeadline({ alsoSetLeague: false })} variant="pink">Apply to {currentGameweekID || "this gameweek"}</Button>
                <Button onClick={() => handleApplyCustomDeadline({ alsoSetLeague: true })}>Save default settings</Button>
                <button type="button" onClick={handleAddWeekToDeadline} disabled={!currentDeadline || !currentGameweekID} style={{ border: "none", background: "transparent", padding: 0, color: "#1976d2", cursor: !currentDeadline || !currentGameweekID ? "not-allowed" : "pointer", fontWeight: 700 }}>
                  + Add 1 week
                </button>
              </div>
            </div>
          )}
          <div style={{ marginTop: "16px", color: "#666" }}>
            <strong>Last Advanced:</strong>{" "}
            {lastAdvancedValue
              ? (lastAdvancedValue.toDate
                  ? lastAdvancedValue.toDate().toLocaleString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
                  : new Date(lastAdvancedValue.seconds * 1000).toLocaleString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }))
              : "—"}
          </div>
        </Panel>
      ) : (
        <Panel style={{ marginTop: "1.5rem", textAlign: "center", color: "#666", background: "#f0f0f0", maxWidth: "700px" }}>
          <strong>Admin Tools Locked</strong><br />
          {currentPhase === "scoring"
            ? "Scoring in progress — configuration locked."
            : currentPhase === "closed"
              ? "Season closed — no further changes allowed"
              : "Match results exist — configuration locked."}
        </Panel>
      )}

      {/* Availability — collapsible */}
      <CollapsibleSection title="Availability" isOpen={openSections.availability} onToggle={() => toggleSection("availability")}>

        {/* Summary row */}
        {(() => {
          const minRequired = parseInt(league?.leagueType || "0", 10) || 0;
          const playing = leagueUsers.filter(u => availabilityResponses[u.id] === "available");
          const notPlaying = leagueUsers.filter(u => availabilityResponses[u.id] === "unavailable");
          const responded = playing.length + notPlaying.length;
          return (
            <div style={{ fontSize: "0.85rem", color: "#888", marginBottom: 16 }}>
              <span>{responded}/{leagueUsers.length} responded</span>
              {" · "}
              <span style={{ color: playing.length >= minRequired && minRequired > 0 ? "#2e7d32" : "#e65100", fontWeight: 600 }}>
                {playing.length}/{minRequired} min. required
              </span>
              {notPlaying.length > 0 && (
                <span>{` · ${notPlaying.length} Can't Make It`}</span>
              )}
            </div>
          );
        })()}

        {/* Member response list */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
          {leagueUsers.map(u => {
            const response = availabilityResponses[u.id];
            const isSaving = savingAvailability[u.id];
            const isSavingLink = savingPlayerLink[u.id];
            const linkedPlayerID = u.linkedPlayerIDs?.[leagueID] || "";
            const linkedPlayer = players.find(p => p.id === linkedPlayerID);

            return (
              <div key={u.id} style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                padding: "10px 14px",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}>
                {/* Row 1: name + availability buttons */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: "0.9rem", flex: 1, minWidth: 0, wordBreak: "break-word", lineHeight: 1.3 }}>
                    {u.displayName || u.email || u.id}
                  </span>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <button
                      type="button"
                      disabled={isSaving}
                      onClick={() => handleAdminSetAvailability(u.id, "available")}
                      style={{
                        padding: "5px 10px", borderRadius: 6, border: "none",
                        fontSize: "0.8rem", fontWeight: 700, cursor: "pointer",
                        background: response === "available" ? "#50B4C8" : "#f0f0f0",
                        color: response === "available" ? "#fff" : "#555",
                      }}
                    >
                      Playing
                    </button>
                    <button
                      type="button"
                      disabled={isSaving}
                      onClick={() => handleAdminSetAvailability(u.id, "unavailable")}
                      style={{
                        padding: "5px 10px", borderRadius: 6, border: "none",
                        fontSize: "0.8rem", fontWeight: 700, cursor: "pointer",
                        background: response === "unavailable" ? "#888" : "#f0f0f0",
                        color: response === "unavailable" ? "#fff" : "#555",
                      }}
                    >
                      Can't Make It
                    </button>
                  </div>
                </div>

                {/* Row 2: player link */}
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: "0.78rem", color: "#888", flexShrink: 0 }}>
                    Fantasy player:
                  </span>
                  {linkedPlayer ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1 }}>
                      <span style={{
                        width: 7, height: 7, borderRadius: "50%",
                        background: "#4CAF50", display: "inline-block", flexShrink: 0,
                      }} />
                      <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "#234" }}>
                        {linkedPlayer.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleAdminLinkPlayer(u.id, "")}
                        disabled={isSavingLink}
                        style={{
                          marginLeft: "auto", background: "none", border: "none",
                          color: "#aaa", fontSize: "0.75rem", cursor: "pointer", padding: 0,
                        }}
                      >
                        unlink
                      </button>
                    </div>
                  ) : (
                    <select
                      value=""
                      disabled={isSavingLink}
                      onChange={(e) => handleAdminLinkPlayer(u.id, e.target.value)}
                      style={{
                        flex: 1, padding: "4px 8px", borderRadius: 6,
                        border: "1px solid #ddd", fontSize: "0.8rem",
                        background: "#fafafa", color: "#555",
                      }}
                    >
                      <option value="">Link to player…</option>
                      {[...players]
                        .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                        .map(p => (
                          <option key={p.id} value={p.id}>
                            {p.name} ({p.position})
                          </option>
                        ))
                      }
                    </select>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Unlinked players — available for this match but not tied to a user account */}
        {(() => {
          const linkedPlayerIDSet = new Set(
            leagueUsers.map(u => u.linkedPlayerIDs?.[leagueID]).filter(Boolean)
          );
          const unlinkedPlayers = players.filter(p => !linkedPlayerIDSet.has(p.id));

          if (unlinkedPlayers.length === 0) return null;

          return (
            <div style={{ marginBottom: 20 }}>
              <div style={{
                fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase",
                letterSpacing: "0.05em", color: "#888", marginBottom: 8, marginTop: 4,
              }}>
                Other players — not linked to ClubWeek account
              </div>
              <div style={{ fontSize: "0.78rem", color: "#aaa", marginBottom: 10 }}>
                Mark players available if they're playing but don't have a Clubweek account
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {[...unlinkedPlayers]
                  .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                  .map(p => {
                    const response = unlinkedAvailability[p.id] || availabilityResponses[p.id];
                    const isSaving = savingUnlinked[p.id];
                    return (
                      <div key={p.id} style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
                        padding: "8px 14px", minHeight: 44, gap: 8,
                      }}>
                        <span style={{ fontWeight: 600, fontSize: "0.88rem", flex: 1, minWidth: 0, wordBreak: "break-word", lineHeight: 1.3 }}>
                          {p.name}
                          <span style={{ fontWeight: 400, color: "#aaa", fontSize: "0.78rem", marginLeft: 6 }}>
                            {p.position}
                          </span>
                        </span>
                        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                          <button
                            type="button"
                            disabled={isSaving}
                            onClick={() => handleUnlinkedPlayerAvailability(p.id, "available")}
                            style={{
                              padding: "5px 10px", borderRadius: 6, border: "none",
                              fontSize: "0.8rem", fontWeight: 700, cursor: "pointer",
                              background: response === "available" ? "#50B4C8" : "#f0f0f0",
                              color: response === "available" ? "#fff" : "#555",
                            }}
                          >
                            Playing
                          </button>
                          <button
                            type="button"
                            disabled={isSaving}
                            onClick={() => handleUnlinkedPlayerAvailability(p.id, "unavailable")}
                            style={{
                              padding: "5px 10px", borderRadius: 6, border: "none",
                              fontSize: "0.8rem", fontWeight: 700, cursor: "pointer",
                              background: response === "unavailable" ? "#888" : "#f0f0f0",
                              color: response === "unavailable" ? "#fff" : "#555",
                            }}
                          >
                            Can't Make It
                          </button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          );
        })()}

        {/* Availability notifications toggle */}
        <div style={{ borderTop: "1px solid #eee", paddingTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>Availability notifications</div>
              <div style={{ fontSize: "0.8rem", color: "#888", marginTop: 2 }}>Send automatic reminders to players before each match</div>
            </div>
            <button
              type="button"
              disabled={savingAvailabilityToggle}
              onClick={handleToggleAvailabilityEnabled}
              style={{
                padding: "7px 16px", borderRadius: 20, border: "none", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer",
                background: availabilityEnabled ? "#50B4C8" : "#e0e0e0",
                color: availabilityEnabled ? "#fff" : "#555",
                opacity: savingAvailabilityToggle ? 0.6 : 1,
              }}
            >
              {availabilityEnabled ? "On" : "Off"}
            </button>
          </div>
        </div>

      </CollapsibleSection>

      {/* Match Results — collapsible */}
      <CollapsibleSection title="Match Results" isOpen={openSections.matchResults} onToggle={() => toggleSection("matchResults")}>
        <div style={{ display: "flex", alignItems: "center", gap: "5px", color: "#888", fontSize: "0.82em", marginBottom: "10px" }}>
          <Info size={13} />
          <span>Fill in or update the opponent and score for any past match</span>
        </div>

        {loadingMatchResults ? (
          <p style={{ color: "#888", fontSize: "0.9em" }}>Loading match history…</p>
        ) : matchResultsData.length === 0 ? (
          <p style={{ color: "#888", fontSize: "0.9em" }}>No match entries found yet.</p>
        ) : (
          (() => {
            // Group the already-sorted flat list (most recent season/GW first) into
            // Season → Gameweek → Match(es), preserving fetch order.
            const seasonOrder = [];
            const bySeason = {};
            matchResultsData.forEach((m) => {
              if (!bySeason[m.seasonID]) {
                bySeason[m.seasonID] = { gwOrder: [], byGW: {} };
                seasonOrder.push(m.seasonID);
              }
              const seasonBucket = bySeason[m.seasonID];
              if (!seasonBucket.byGW[m.gwID]) {
                seasonBucket.byGW[m.gwID] = [];
                seasonBucket.gwOrder.push(m.gwID);
              }
              seasonBucket.byGW[m.gwID].push(m);
            });

            return seasonOrder.map((seasonID, seasonIdx) => {
              const seasonBucket = bySeason[seasonID];
              const isSeasonOpen =
                openMatchSeasons[seasonID] !== undefined ? openMatchSeasons[seasonID] : seasonIdx === 0;
              return (
                <div key={seasonID} style={{ marginBottom: 20 }}>
                  <div
                    onClick={() => setOpenMatchSeasons((prev) => ({ ...prev, [seasonID]: !isSeasonOpen }))}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      cursor: "pointer", userSelect: "none",
                      fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase",
                      letterSpacing: "0.05em", color: "#888", marginBottom: 8, marginTop: 4,
                    }}
                  >
                    <span>Season {seasonID}</span>
                    {isSeasonOpen ? <ChevronUp size={16} color="#888" /> : <ChevronDown size={16} color="#888" />}
                  </div>
                  {isSeasonOpen && seasonBucket.gwOrder.map((gwID) => (
                    <div key={gwID} style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "#555", marginBottom: 6 }}>
                        {gwID}
                      </div>
                      {seasonBucket.byGW[gwID].map((m) => {
                        const hasChanged = matchHasChanged(m);
                        const isSaving = savingMatchKey === m.key;
                        const yourScoreVal = getMatchFieldValue(m, "yourScore");
                        const theirScoreVal = getMatchFieldValue(m, "theirScore");
                        const liveResult = computeMatchResult(yourScoreVal, theirScoreVal);

                        return (
                          <div key={m.key} style={{
                            background: hasChanged ? "rgba(255, 193, 7, 0.12)" : "#fff",
                            border: "1px solid #e5e7eb",
                            borderRadius: 10,
                            padding: "10px 14px",
                            marginBottom: 8,
                            transition: "background 0.2s",
                          }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                              {hasChanged && (
                                <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#f59e0b", flexShrink: 0 }} />
                              )}
                              <span style={{ fontWeight: 700, fontSize: "0.85rem" }}>{m.matchLabel}</span>
                              {liveResult && (
                                <span style={{
                                  display: "inline-block", padding: "1px 9px", borderRadius: "999px",
                                  background: `${RESULT_COLORS[liveResult]}1A`, color: RESULT_COLORS[liveResult],
                                  fontWeight: 700, fontSize: "0.75rem",
                                }}>
                                  {liveResult}
                                </span>
                              )}
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                              <input
                                type="text"
                                placeholder="Opponent"
                                value={getMatchFieldValue(m, "opponent")}
                                disabled={isSaving}
                                onChange={(e) => setMatchField(m, "opponent", e.target.value)}
                                style={{ flex: "1 1 140px", minWidth: 100, padding: "6px 8px", borderRadius: 6, border: "1px solid #ddd", fontSize: "0.85rem" }}
                              />
                              <input
                                type="number"
                                placeholder="Your score"
                                value={yourScoreVal}
                                disabled={isSaving}
                                onChange={(e) => setMatchField(m, "yourScore", e.target.value)}
                                style={{ width: 90, padding: "6px 8px", borderRadius: 6, border: "1px solid #ddd", fontSize: "0.85rem" }}
                              />
                              <span style={{ color: "#aaa" }}>–</span>
                              <input
                                type="number"
                                placeholder="Their score"
                                value={theirScoreVal}
                                disabled={isSaving}
                                onChange={(e) => setMatchField(m, "theirScore", e.target.value)}
                                style={{ width: 90, padding: "6px 8px", borderRadius: 6, border: "1px solid #ddd", fontSize: "0.85rem" }}
                              />
                              <button
                                type="button"
                                onClick={() => handleSaveMatchResult(m)}
                                disabled={!hasChanged || isSaving}
                                className={hasChanged && !isSaving ? "save-pulse" : ""}
                                style={{
                                  padding: "6px 14px",
                                  fontSize: "0.8rem",
                                  borderRadius: 6,
                                  border: "none",
                                  background: !hasChanged || isSaving ? "#e5e7eb" : "#E07856",
                                  color: !hasChanged || isSaving ? "#aaa" : "#fff",
                                  fontWeight: 700,
                                  cursor: !hasChanged || isSaving ? "default" : "pointer",
                                }}
                              >
                                {isSaving ? "Saving…" : "Save"}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              );
            });
          })()
        )}

        {Object.keys(pendingMatchEdits).length > 0 && (
          <div style={{ marginTop: "0.85rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "5px", color: "#f59e0b", fontSize: "0.83rem", fontWeight: 600, marginBottom: "8px" }}>
              <AlertCircle size={14} color="#f59e0b" />
              You have unsaved changes
            </div>
            <button
              type="button"
              onClick={handleSaveAllMatchResults}
              disabled={savingAllMatches}
              style={{
                width: "100%", padding: "11px", borderRadius: "8px", border: "none",
                background: savingAllMatches ? "#a0d8e2" : "#50B4C8", color: "#fff",
                fontWeight: 700, fontSize: "0.95rem", cursor: savingAllMatches ? "default" : "pointer",
              }}
            >
              {savingAllMatches ? "Saving..." : "Save All"}
            </button>
          </div>
        )}
      </CollapsibleSection>

      {/* League Members — collapsible */}
      <CollapsibleSection title="League Members" isOpen={openSections.members} onToggle={() => toggleSection("members")}>
        <p style={{ color: "#666", fontSize: "0.9em", marginBottom: "0.85rem", marginTop: 0 }}>
          Promote or remove admins. Only promote people you trust
        </p>
        {loadingLeagueUsers ? (
          <PageLoader message="Loading league members…" />
        ) : leagueUsers.length === 0 ? (
          <p style={{ color: "#666" }}>No members found for this league yet.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
            {leagueUsers.map((u) => {
              const role = getUserRoleForLeague(u);
              const label = u.displayName || u.email || u.id;
              const isUpdating = updatingRoleUserID === u.id;
              const currentUID = getAuth().currentUser?.uid;
              const isSelf = u.id === currentUID;
              const canRemove = !(role === "admin" && adminCount <= 1);
              return (
                <div key={u.id} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: "12px", padding: "14px 16px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: u.email && u.displayName ? "3px" : "10px" }}>
                    <div style={{ fontWeight: 700, fontSize: "0.97em" }}>{label}</div>
                    <span style={{
                      display: "inline-block", padding: "3px 10px", borderRadius: "999px",
                      fontWeight: 700, fontSize: "0.82em",
                      background: role === "admin" ? "#ffe6f2" : "#f3f3f3",
                      color: role === "admin" ? "#c2185b" : "#444",
                    }}>{role}</span>
                  </div>
                  {u.email && u.displayName && (
                    <div style={{ fontSize: "0.84em", color: "#999", marginBottom: "10px" }}>{u.email}</div>
                  )}
                  {!isSelf && (
                    <button
                      type="button"
                      onClick={() => updateUserRoleForLeague(u.id, role === "admin" ? "user" : "admin")}
                      disabled={isUpdating || (role === "admin" && !canRemove)}
                      style={{
                        width: "100%", padding: "9px", borderRadius: "8px",
                        fontWeight: 700, fontSize: "0.88em", cursor: "pointer",
                        border: role === "admin" ? "1px solid #ccc" : "none",
                        background: role === "admin" ? "transparent" : "#E07856",
                        color: role === "admin" ? "#555" : "#fff",
                        opacity: (isUpdating || (role === "admin" && !canRemove)) ? 0.45 : 1,
                        transition: "opacity 0.15s",
                      }}
                    >
                      {isUpdating ? "Updating..." : role === "admin" ? "Remove Admin" : "Make Admin"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CollapsibleSection>

      {/* Players — collapsible */}
      <CollapsibleSection title="Players" isOpen={openSections.players} onToggle={() => toggleSection("players")}>
        <div style={{ display: "flex", alignItems: "center", gap: "5px", color: "#888", fontSize: "0.82em", marginBottom: "10px" }}>
          <Info size={13} />
          <span>Position changes apply from next GW only</span>
        </div>
        {players.length > 0 ? (
          <div style={{ width: "100%" }}>
            <table
              className="players-tight-table"
              style={{ width: "100%", tableLayout: "auto", borderCollapse: "collapse" }}
            >
              <thead>
                <tr>
                  <th style={{ width: "1%", textAlign: "center", padding: "8px 6px", borderBottom: "1px solid #e5e5e5", whiteSpace: "nowrap", background: "transparent", fontWeight: 700 }}>Player</th>
                  <th style={{ width: "1%", textAlign: "center", padding: "8px 6px", borderBottom: "1px solid #e5e5e5", whiteSpace: "nowrap", background: "transparent", fontWeight: 700 }}>Pos</th>
                  <th style={{ width: "1%", textAlign: "center", padding: "8px 6px", borderBottom: "1px solid #e5e5e5", whiteSpace: "nowrap", background: "transparent", fontWeight: 700 }}>Price</th>
                  <th style={{ width: "1%", textAlign: "center", padding: "8px 6px", borderBottom: "1px solid #e5e5e5", whiteSpace: "nowrap", background: "transparent", fontWeight: 700 }}>Rating</th>
                  <th style={{ width: "1%", textAlign: "center", padding: "8px 6px", borderBottom: "1px solid #e5e5e5", whiteSpace: "nowrap", background: "transparent", fontWeight: 700 }}></th>
                </tr>
              </thead>
              <tbody>
                {players.map((p) => {
                  const selectedPosition = pendingPlayerPositions[p.id] || p.position;
                  const hasChanged = selectedPosition !== p.position;
                  const isUpdating = updatingPlayerPositionID === p.id;

                  return (
                    <tr
                      key={p.id}
                      style={{ background: hasChanged ? "rgba(255, 193, 7, 0.12)" : "transparent", transition: "background 0.2s" }}
                    >
                      <td style={{ padding: "8px 6px", borderBottom: "1px solid #f0f0f0", whiteSpace: "nowrap", textAlign: "center" }}>
                        {hasChanged && (
                          <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#f59e0b", marginRight: 6, verticalAlign: "middle" }} />
                        )}
                        {p.name}
                      </td>
                      <td style={{ padding: "8px 6px", borderBottom: "1px solid #f0f0f0", whiteSpace: "nowrap", textAlign: "center" }}>
                        <select
                          value={selectedPosition}
                          onChange={(e) =>
                            setPendingPlayerPositions((prev) => ({ ...prev, [p.id]: e.target.value }))
                          }
                          disabled={isUpdating}
                          style={{ padding: "6px 8px", borderRadius: "8px", border: "1px solid #ddd", fontWeight: 600 }}
                        >
                          <option value="DEF">DEF</option>
                          <option value="MID">MID</option>
                          <option value="FWD">FWD</option>
                        </select>
                      </td>
                      <td style={{ padding: "8px 6px", borderBottom: "1px solid #f0f0f0", textAlign: "center", whiteSpace: "nowrap" }}>
                        ${Number(p.currentPrice ?? p.price ?? 0).toFixed(1)}m
                      </td>
                      <td style={{ padding: "8px 6px", borderBottom: "1px solid #f0f0f0", textAlign: "center", whiteSpace: "nowrap" }}>
                        {p.rating}
                      </td>
                      <td style={{ padding: "8px 6px", borderBottom: "1px solid #f0f0f0", textAlign: "center", whiteSpace: "nowrap" }}>
                        <button
                          type="button"
                          onClick={() => handleUpdatePlayerPosition(p.id)}
                          disabled={!hasChanged || isUpdating}
                          className={hasChanged && !isUpdating ? "save-pulse" : ""}
                          style={{
                            padding: "6px 10px",
                            fontSize: "0.8rem",
                            borderRadius: "6px",
                            border: "none",
                            background: !hasChanged || isUpdating ? "#e5e7eb" : "#E07856",
                            color: !hasChanged || isUpdating ? "#aaa" : "#fff",
                            fontWeight: 700,
                            cursor: !hasChanged || isUpdating ? "default" : "pointer",
                          }}
                        >
                          {isUpdating ? "Saving…" : "Save"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No players added yet.</p>
        )}

        {Object.keys(pendingPlayerPositions).length > 0 && (
          <div style={{ marginTop: "0.85rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "5px", color: "#f59e0b", fontSize: "0.83rem", fontWeight: 600, marginBottom: "8px" }}>
              <AlertCircle size={14} color="#f59e0b" />
              You have unsaved changes
            </div>
            <button
              type="button"
              onClick={handleSaveAll}
              disabled={savingAll}
              style={{
                width: "100%", padding: "11px", borderRadius: "8px", border: "none",
                background: savingAll ? "#a0d8e2" : "#50B4C8", color: "#fff",
                fontWeight: 700, fontSize: "0.95rem", cursor: savingAll ? "default" : "pointer",
              }}
            >
              {savingAll ? "Saving..." : "Save All"}
            </button>
          </div>
        )}

        {["pick", "results"].includes(currentPhase?.toLowerCase()) && (
          <div style={{ marginTop: "1.25rem", paddingTop: "1.25rem", borderTop: "1px solid #eee" }}>
            <h4 style={{ margin: "0 0 0.65rem 0", fontWeight: 700 }}>Add Player</h4>
            <div style={{ marginBottom: "0.75rem", display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "center" }}>
              <input
                type="text"
                placeholder="Name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                style={{ minWidth: "140px", flex: "1 1 140px" }}
              />
              <Dropdown
                value={newPosition}
                onChange={(opt) => setNewPosition(opt)}
                options={[{ value: "DEF", label: "DEF" }, { value: "MID", label: "MID" }, { value: "FWD", label: "FWD" }]}
                placeholder="Select Position"
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                style={{ minWidth: "150px", flex: "1 1 150px" }}
              />
              <Dropdown
                value={newRating}
                onChange={(opt) => setNewRating(opt)}
                options={[1, 2, 3, 4, 5].map(r => ({ value: r, label: String(r) }))}
                placeholder="Select Rating"
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                style={{ minWidth: "150px", flex: "1 1 150px" }}
              />
            </div>
            <Button
              onClick={handleAddPlayer}
              disabled={!newName || !newPosition || !newRating || addingPlayer}
            >
              {addingPlayer ? "Adding..." : "Add Player"}
            </Button>
          </div>
        )}
      </CollapsibleSection>

      {/* End Season — collapsible */}
      <CollapsibleSection title="End Season" isOpen={openSections.endSeason} onToggle={() => toggleSection("endSeason")}>
        <p style={{ color: "#b00020", fontWeight: 600, marginTop: 0, marginBottom: "14px" }}>
          This cannot be undone
        </p>
        <button
          type="button"
          onClick={() => navigate(`/endofseason/${leagueID}`)}
          style={{
            padding: "10px 14px",
            borderRadius: "10px",
            border: "2px solid #d9534f",
            background: "transparent",
            color: "#d9534f",
            fontWeight: 900,
            cursor: "pointer",
          }}
        >
          End Season
        </button>
      </CollapsibleSection>

      {/* Match results bottom sheet */}
      {showMatchSheet && (
        <>
          <div
            onClick={() => setShowMatchSheet(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.42)", zIndex: 999 }}
          />
          <div style={{
            position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 1000,
            background: "#fff", borderRadius: "20px 20px 0 0",
            padding: "1.5rem 1rem 2.5rem",
            boxShadow: "0 -4px 24px rgba(0,0,0,0.15)",
          }}>
            <h3 style={{ margin: "0 0 1rem 0", textAlign: "center", fontWeight: 800 }}>
              {hasMatchResults ? "Edit Match Results" : "Enter Match Results"}
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {matchList.length > 0 ? (
                matchList.map((match) => (
                  <button
                    key={match.id}
                    onClick={() => { setShowMatchSheet(false); navigate(`/matchinput/${leagueID}/${currentGameweekID}/${match.id}`); }}
                    style={{
                      padding: "14px 16px", borderRadius: "10px",
                      border: "1px solid #e5e7eb", background: "#f9f9f9",
                      fontWeight: 700, cursor: "pointer", fontSize: "1em",
                      textAlign: "left", width: "100%",
                    }}
                  >
                    {match.label}
                  </button>
                ))
              ) : (
                <button
                  onClick={() => { setShowMatchSheet(false); navigate(`/matchinput/${leagueID}/${currentGameweekID}`); }}
                  style={{
                    padding: "14px 16px", borderRadius: "10px",
                    border: "1px solid #e5e7eb", background: "#f9f9f9",
                    fontWeight: 700, cursor: "pointer", fontSize: "1em",
                    textAlign: "left", width: "100%",
                  }}
                >
                  Create Match
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* Advance prompt overlay */}
      {showAdvancePrompt && currentPhase === "results" && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 }}>
          <div style={{ backgroundColor: "white", padding: "20px", borderRadius: "8px", maxWidth: "420px", textAlign: "center" }}>
            <h3 style={{ marginTop: 0 }}>Results saved</h3>
            <p style={{ marginBottom: "1rem" }}>You can add another match, edit results, or advance the gameweek.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <Button onClick={() => { setShowAdvancePrompt(false); navigate(`/matchinput/${leagueID}/${currentGameweekID}/new`); }} disabled={runningAdvance}>Add Another Match</Button>
              <Button onClick={() => setShowAdvancePrompt(false)} disabled={runningAdvance}>Edit / Review Results</Button>
              <Button onClick={scoringAndAdvanceGameweek} disabled={runningAdvance} variant="pink">
                {runningAdvance ? "Advancing..." : "Confirm and Advance"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm modal */}
      {confirmModal.open && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 }}>
          <div style={{ backgroundColor: "white", padding: "20px", borderRadius: "8px", maxWidth: "420px", width: "90%", textAlign: "center" }}>
            <h3 style={{ marginTop: 0 }}>Confirm Action</h3>
            <p style={{ marginBottom: "1rem" }}>{confirmModal.message}</p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
              <Button onClick={closeConfirmModal}>Cancel</Button>
              <Button onClick={handleConfirm} variant="pink">Confirm</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageLeague;
